import React, { useEffect, useState } from 'react';

import AdContainer from '@/components/ads/ad-container';
import AdContainerIds from '@/components/ads/ad-container-ids';
import SingleSelectDropdown, { SingleSelectInputOption } from '@/components/display/select/single-select-dropdown';
import Newsletter from '@/components/pages/frontpage/components/feature/right/newsletter';
import NewsRecirculationSection from '@/components/sections/content/news-recirculation-section';
import subscribeToNewsletter from '@/utils/functions/subscribe-to-newsletter';

import EtfPageHeader, { Asset } from './etf-page-header';
import EtfPageTable, { ETFEntity } from './etf-page-table';
import EtfPageWhatIs from './etf-page-what-is';

type Props = {
    etfs: ETFEntity[];
    lastUpdated?: number;
    asset: Asset;
    explanationPageHtml: string | null;
    sponsoredEtfs: ETFEntity[];
};

const EtfPageLayout = (props: Props) => {
    const { asset, etfs, lastUpdated, explanationPageHtml, sponsoredEtfs } = props;

    const [filteredEtfs, setFilteredEtfs] = useState(etfs);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState<SingleSelectInputOption | null>(null);
    useEffect(() => {
        let etfFilter = etfs;
        const lowerSearchTerm = searchTerm.toLowerCase();
        etfFilter = etfFilter.filter(row => {
            return (
                row.ticker.toLowerCase().includes(lowerSearchTerm) ||
                row.custodian.toLowerCase().includes(lowerSearchTerm) ||
                row.etfName.toLowerCase().includes(lowerSearchTerm)
            );
        });

        if (selectedType?.id) {
            etfFilter = etfFilter.filter(row => {
                return row.type.toLowerCase().includes(selectedType.id.toLowerCase());
            });
        }

        setFilteredEtfs(etfFilter);
    }, [etfs, searchTerm, selectedType]);

    return (
        <main className="border-gray-200 w-full">
            <section className="flex flexRow flexWrap xl:flex-nowrap overflow-hidden">
                <div className="h-full p-0 xl:borderR border-b xl:border-b-0 w-full">
                    <div className="flex flex-col space-y-4 p-6 lg:p-10 ">
                        <EtfPageHeader asset={asset} etfs={filteredEtfs} lastUpdated={lastUpdated} />

                        <div className="flex flex-col xs:flex-row w-full justify-start items-start self-stretch relative gap-2">
                            <div className="flex-grow w-full md:w-2/4 lg:w-2/3">
                                <input
                                    id="searchInput"
                                    type="text"
                                    onChange={ev => {
                                        setSearchTerm(ev.target.value);
                                    }}
                                    placeholder="Search..."
                                    className="w-full min-w-0 appearance-none rounded-none border border-gray-200 hover:border-gray-300 bg-white py-2.5 px-4 text-sm text-left text-gray-900 placeholder-gray-400 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                                />
                            </div>
                            <div className="flex flex-col justify-start relative gap-1 w-full md:w-2/4 lg:w-1/3">
                                <SingleSelectDropdown
                                    options={[
                                        { id: '', name: 'All' },
                                        { id: 'Spot', name: 'Spot' },
                                        { id: 'Futures', name: 'Futures' },
                                    ]}
                                    label=""
                                    placeholder="Select type to filter"
                                    onChange={setSelectedType}
                                    value={selectedType}
                                    className="min-w-24 "
                                />
                            </div>
                        </div>
                        <div className="flex flex-col w-full">
                            <EtfPageTable etfs={filteredEtfs} sponsoredEtfs={sponsoredEtfs} />
                        </div>
                    </div>
                    <hr className="border-gray-200" />
                    <section className="relative flex flex-row flex-wrap lg:flex-nowrap">
                        {/* vertical border */}
                        <div className="hidden lg:block absolute top-0 bottom-0 left-[66.66%] w-px bg-gray-200 z-10" />

                        <div className="basis-full lg:basis-4/6 border-b lg:border-b-0">
                            <div className="p-5 md:p-8 lg:p-10">
                                <EtfPageWhatIs pageContent={explanationPageHtml ?? ''} />
                            </div>
                            <NewsRecirculationSection
                                algoliaSearchKeyword={`etf -${asset.title.toLowerCase()}`}
                                title="latest etf news"
                                count={16}
                                paddingClassName="p-8"
                                className="p-0"
                                imageQuality={10}
                            />
                        </div>

                        <div className="basis-full lg:basis-2/6 lg:min-w-[300px]">
                            <div>
                                <Newsletter
                                    title="Newsletter"
                                    subTitle="The Breakdown"
                                    description="Decoding crypto and the markets. Daily, with Byron Gilliam."
                                    inputPlaceholder="Email address"
                                    buttonText="Subscribe"
                                    onButtonClick={subscribeToNewsletter}
                                />
                            </div>
                            <div>
                                <NewsRecirculationSection
                                    algoliaSearchKeyword={`${asset.title.toLowerCase()} etf`}
                                    title={`${asset.title.toLowerCase()} etf in the news`}
                                    style="vertical"
                                    count={2}
                                    paddingClassName="p-8"
                                    className="p-0"
                                />
                            </div>
                            <div className="p-6 xl:p-12">
                                <AdContainer
                                    googleAd={AdContainerIds.NewsArticleRight600x300!}
                                    className="hidden lg:flex h-[600px] w-[300px] text-center align-middle mx-auto"
                                />
                            </div>
                            <div>
                                <NewsRecirculationSection
                                    algoliaSearchKeyword={`${asset.title.toLowerCase()} etf`}
                                    style="vertical"
                                    count={2}
                                    page={1}
                                    paddingClassName="p-8"
                                    className="p-0"
                                />
                            </div>
                        </div>
                    </section>
                </div>
            </section>
        </main>
    );
};

export default EtfPageLayout;
